import React, { useCallback, useEffect, useMemo } from 'react';

import { FloatingPortal } from '@floating-ui/react';
import { Bell, EyeOff, Settings, Users } from 'lucide-react';
import { useHotkeys } from 'react-hotkeys-hook';

import { KEYCHAIN, useRegisterWithKeyMaster } from '../../contexts/KeyMaster';
import { useListNavigation } from '../../hooks/useListNavigation';
import { GlassWrapper } from '../shared/GlassWrapper';
import { MenuButton } from '../shared/MenuButton';
import { useContextMenu } from './useContextMenu';

const WORKSPACE_CONTEXT_MENU_ITEMS = {
  SETTINGS: 'settings',
  NOTIFICATIONS: 'notifications',
  MEMBERS: 'members',
  HIDE_DIRECT_MESSAGE: 'hide_direct_message',
};

export function WorkspaceRailContextMenu({
  position,
  onClose,
  onSettings,
  onNotifications,
  onManageMembers,
  onHideDirectMessage = null,
}) {
  const { refs, floatingStyles, getFloatingProps } = useContextMenu({
    position,
    onClose,
  });
  const { unlocked } = useRegisterWithKeyMaster(KEYCHAIN.POPOVER__SIDEBAR_CONTEXT_MENU);

  useEffect(() => {
    function handleToggleContextMenu(event) {
      if (!event.detail?.isOpen) onClose();
    }

    window.addEventListener('output:close-context-menu', onClose);
    window.addEventListener('output:toggle-context-menu', handleToggleContextMenu);

    return () => {
      window.removeEventListener('output:close-context-menu', onClose);
      window.removeEventListener('output:toggle-context-menu', handleToggleContextMenu);
    };
  }, [onClose]);

  const menuItems = useMemo(
    () =>
      onHideDirectMessage
        ? [
            {
              key: WORKSPACE_CONTEXT_MENU_ITEMS.HIDE_DIRECT_MESSAGE,
              label: 'Hide',
              icon: EyeOff,
              handler: onHideDirectMessage,
            },
          ]
        : [
            {
              key: WORKSPACE_CONTEXT_MENU_ITEMS.SETTINGS,
              label: 'Settings',
              icon: Settings,
              handler: onSettings,
            },
            {
              key: WORKSPACE_CONTEXT_MENU_ITEMS.NOTIFICATIONS,
              label: 'Notifications',
              icon: Bell,
              handler: onNotifications,
            },
            {
              key: WORKSPACE_CONTEXT_MENU_ITEMS.MEMBERS,
              label: 'Manage members',
              icon: Users,
              handler: onManageMembers,
            },
          ],
    [onHideDirectMessage, onManageMembers, onNotifications, onSettings],
  );

  const handleItemSelect = useCallback(function handleWorkspaceRailContextMenuItemSelect(item) {
    item.handler();
  }, []);

  const { selectedIndex, createMouseEnterHandler, onMouseLeave, onKeyDown } = useListNavigation({
    items: menuItems,
    onSelect: handleItemSelect,
    wrap: true,
    initialIndex: 0,
  });

  const handleEscapeHotkey = useCallback(
    function handleWorkspaceRailContextMenuEscape() {
      if (unlocked()) onClose();
    },
    [onClose, unlocked],
  );

  const handleNavigationHotkey = useCallback(
    function handleWorkspaceRailContextMenuNavigation(event) {
      if (!unlocked()) return;
      onKeyDown({ event });
    },
    [onKeyDown, unlocked],
  );

  useHotkeys('esc', handleEscapeHotkey, { enableOnFormTags: true, preventDefault: true }, [handleEscapeHotkey]);

  useHotkeys('up,down,enter', handleNavigationHotkey, { enableOnFormTags: true, preventDefault: true }, [handleNavigationHotkey]);

  return (
    <FloatingPortal>
      <GlassWrapper
        ref={refs.setFloating}
        style={floatingStyles}
        {...getFloatingProps({ role: 'menu' })}
        className="z-(--stack-context-menu) w-40 gap-0 whitespace-nowrap"
        onMouseLeave={onMouseLeave}
      >
        {menuItems.map((item, index) => {
          const Icon = item.icon;
          return (
            <MenuButton
              key={item.key}
              role="menuitem"
              isActive={selectedIndex === index}
              onClick={item.handler}
              onMouseMove={createMouseEnterHandler(index)}
            >
              <span className="flex items-center gap-2.5">
                <Icon size={12} aria-hidden="true" />
                {item.label}
              </span>
            </MenuButton>
          );
        })}
      </GlassWrapper>
    </FloatingPortal>
  );
}
